import { forwardRef, useState, useEffect } from 'react';
import type { Ref, PropsWithoutRef, ComponentType, ForwardRefExoticComponent } from 'react';
import styled, { css } from 'styled-components';

import { useTheme } from '../../hooks';
import type { NoChildrenProp, RefElement, WithAttributes } from '../../types';
import { defaultThemeProp } from '../../theme';
import { readableColor } from '../../styles';

type BaseIconProps = NoChildrenProp & {
  /**
   * The name of the icon set.
   * @default theme.base['icon-set']
   */
  set?: string;
  /** The name indicating the Cosmos Icon. Reference the catalog above for the available names. */
  name: string;
  /**
   * Background & svg size.
   * @default "s" without background, "m" with background.
   */
  size?: 's' | 'm' | 'l' | 'font-size';
};

export type IconProps =
  | WithAttributes<
      'div',
      BaseIconProps & {
        /** Background color. */
        background: string;
        /**
         * Foreground color (svg fill).
         * Applies readable color off background if undefined.
         */
        foreground?: string;
        /**
         * Shape of the background.
         * @default 'square'
         */
        shape?: 'square' | 'circle';
      }
    >
  | WithAttributes<
      'svg',
      BaseIconProps & {
        background?: never;
        foreground?: never;
        shape?: never;
      }
    >;

export interface IconDefinition {
  Component: ComponentType;
  viewBox?: string;
}

export interface IconModule extends IconDefinition {
  set?: IconProps['set'];
  name: IconProps['name'];
}

const iconRegistry = new Map<string, Map<string, IconDefinition>>([]);

export const registerIcon = (...icons: IconModule[]) => {
  icons.forEach(({ set: modSet, name, ...iconDef }) => {
    const set = modSet ?? 'budicon';

    if (!iconRegistry.has(set)) {
      iconRegistry.set(set, new Map([[name, iconDef]]));
    } else if (!iconRegistry.get(set)!.has(name)) {
      iconRegistry.get(set)!.set(name, iconDef);
    }
  });
};

export const forceRegisterIcon = (...icons: IconModule[]) => {
  icons.forEach(({ set: modSet, name, ...iconDef }) => {
    const set = modSet ?? 'budicon';

    if (!iconRegistry.has(set)) {
      iconRegistry.set(set, new Map([[name, iconDef]]));
    } else {
      iconRegistry.get(set)!.set(name, iconDef);
    }
  });
};

export const StyledIcon = styled.svg<{ size?: IconProps['size'] }>(({ theme, size = 's' }) => {
  const {
    components: {
      icon: {
        size: { s, m, l }
      }
    }
  } = theme;
  return css`
    display: inline-block;
    fill: currentColor;
    vertical-align: middle;
    flex-shrink: 0;
    /* stylelint-disable unit-allowed-list */
    min-width: 14px;
    min-height: 14px;
    /* stylelint-enable unit-allowed-list */

    @media (forced-colors: active) {
      fill: CanvasText;
    }

    ${size === 's' &&
    css`
      width: ${s};
      height: ${s};
    `}

    ${size === 'm' &&
    css`
      width: ${m};
      height: ${m};
    `}

    ${size === 'l' &&
    css`
      width: ${l};
      height: ${l};
    `}

    ${size === 'font-size' &&
    css`
      width: 1em;
      height: 1em;
    `}
  `;
});

StyledIcon.defaultProps = defaultThemeProp;

export const StyledIconShape = styled.div<{
  background: NonNullable<IconProps['background']>;
  foreground?: IconProps['foreground'];
  size?: IconProps['size'];
  shape?: IconProps['shape'];
}>(({ theme, background, foreground, size = 'm', shape = 'square' }) => {
  const readableFg = readableColor(background, { level: 'AANonText' });

  const {
    base: { 'border-radius': baseRadius },
    components: {
      icon: {
        size: { s, m, l },
        'border-radius-multiplier': borderRadiusMultiplier
      }
    }
  } = theme;

  return css`
    color: ${foreground ?? readableFg};
    background-color: ${background};
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: clip;

    ${size === 's' &&
    css`
      width: ${s};
      height: ${s};
    `}

    ${size === 'm' &&
    css`
      width: ${m};
      height: ${m};
    `}

    ${size === 'l' &&
    css`
      width: ${l};
      height: ${l};
    `}

    ${size === 'font-size' &&
    css`
      width: 1em;
      height: 1em;
    `}

    ${shape === 'square' &&
    css`
      border-radius: calc(${borderRadiusMultiplier} * ${baseRadius});
    `}

    ${shape === 'circle' &&
    css`
      border-radius: 50%;
    `}

    ${StyledIcon} {
      width: 50%;
      height: 50%;
      /* stylelint-disable unit-allowed-list */
      min-width: min(14px, 90%);
      min-height: min(14px, 90%);
      /* stylelint-enable unit-allowed-list */
    }
  `;
});

StyledIconShape.defaultProps = defaultThemeProp;

const emptyIconDefinition: Readonly<IconDefinition> = Object.freeze({ Component: () => null });

const Icon = forwardRef<RefElement<IconProps>, PropsWithoutRef<IconProps>>(function Icon(
  {
    set: setProp,
    name,
    size,
    'aria-label': ariaLabel,
    role,
    ...restProps
  }: PropsWithoutRef<IconProps>,
  ref
) {
  const {
    base: { 'icon-set': setTheme }
  } = useTheme();

  const set = setProp ?? setTheme ?? 'budicon';

  const [iconDef, setIconDef] = useState(iconRegistry.get(set)?.get(name) ?? emptyIconDefinition);

  useEffect(() => {
    if (iconRegistry.get(set)?.has(name)) {
      setIconDef(iconRegistry.get(set)!.get(name)!);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        const encodedName = encodeURIComponent(name);
        let iconSet: string;
        let module: IconModule;

        switch (set) {
          case 'streamline':
            iconSet = 'streamline-icons';
            break;
          case 'budicon':
            iconSet = 'icons';
            break;
          default:
            throw new Error('Unknown icon');
        }

        // Different extensions are required for different build setups.
        try {
          module = await import(`./${iconSet}/${encodedName}.icon`);
        } catch {
          try {
            module = await import(`./${iconSet}/${encodedName}.icon.tsx`);
          } catch {
            module = await import(`./${iconSet}/${encodedName}.icon.js`);
          }
        }

        if (module.name !== name || !module.Component) throw new Error('Malformed icon definition');

        registerIcon(module);
      } catch {
        registerIcon({ set, name, ...emptyIconDefinition });
      }

      if (!controller.signal.aborted) {
        setIconDef(iconRegistry.get(set)!.get(name) ?? emptyIconDefinition);
      }
    })();

    return () => controller.abort();
  }, [name, set]);

  return typeof restProps.background === 'string' ? (
    <StyledIconShape
      {...restProps}
      background={restProps.background}
      foreground={restProps.foreground}
      shape={restProps.shape}
      size={size}
      ref={ref as Ref<HTMLDivElement>}
    >
      <StyledIcon
        role={role ?? (ariaLabel === undefined ? 'presentation' : 'img')}
        aria-label={ariaLabel}
        viewBox={iconDef.viewBox}
      >
        <iconDef.Component />
      </StyledIcon>
    </StyledIconShape>
  ) : (
    <StyledIcon
      {...restProps}
      role={role ?? (ariaLabel === undefined ? 'presentation' : 'img')}
      aria-label={ariaLabel}
      viewBox={iconDef.viewBox}
      size={size}
      ref={ref as Ref<SVGSVGElement>}
    >
      <iconDef.Component />
    </StyledIcon>
  );
}) as ForwardRefExoticComponent<IconProps>;

export default Icon;
